import {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {useVoucherPricing} from '@udemy/react-certification-vouchers';
import {CertificationVoucherBuyable, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import {useBundleForCourseUpsell} from 'src/hooks/use-bundle-for-course-upsell';
import {useCoursePrice} from 'src/hooks/use-course-price';
import {CartUpsellFeatureVariation} from 'src/lib/experimentation-helper';

import {
    buildShoppingItemFromGqlCourse,
    buildVoucherShoppingItemFromPool,
    isEffectivelyFree,
} from '../certification-upsell-shopping-item-builders';
import {CertificationBundle} from '../certification-upsell.types';

interface ResolvedUpsell {
    otherCourseId: number;
    voucherItem: UdemyShoppingItem;
}

export interface UseCertificationCourseUpsellParams {
    item: UdemyShoppingItem;
    cartCourseIds?: Set<number>;
    cartVoucherOfferIds?: Set<string>;
    featureVariation: CartUpsellFeatureVariation;
}

export interface UseCertificationCourseUpsellResult {
    resolved: ResolvedUpsell | null;
    otherCourseItem: UdemyShoppingItem | null;
    showSuggestedCourse: boolean;
    showVoucher: boolean;
    showConnector: boolean;
    hasContent: boolean;
    voucherOfferId: string | undefined;
    isLoading: boolean;
}

export function useCertificationCourseUpsell({
    item,
    cartCourseIds,
    cartVoucherOfferIds,
    featureVariation,
}: UseCertificationCourseUpsellParams): UseCertificationCourseUpsellResult {
    const {gettext, interpolate} = useI18n();
    const courseId = item.buyable.id;

    const {bundle, bundleLoading, relatedTopicId, examInTopic, otherCourseInBundle} =
        useBundleForCourseUpsell(courseId);
    const {course: otherCourse, isLoading: otherCourseLoading} = otherCourseInBundle;

    const examIsLoading = Boolean(relatedTopicId) && examInTopic.isLoading;

    const voucherPool =
        !examIsLoading && relatedTopicId
            ? examInTopic.voucherPool ?? examInTopic.defaultPool
            : null;

    const voucherPricing = useVoucherPricing({
        voucherPool: examInTopic.voucherPool,
        retakeInsurancePool: examInTopic.retakeInsurancePool,
        defaultPool: examInTopic.defaultPool,
        hasBothPoolsAvailable: examInTopic.hasBothPoolsAvailable,
        isExamLoading: examIsLoading,
    });

    const {selectedPrice, retakeInsurancePrice} = voucherPricing;

    const resolved = useMemo((): ResolvedUpsell | null => {
        if (!voucherPool) return null;

        const titleTemplate = retakeInsurancePrice
            ? gettext('%(title)s Exam Voucher + Retake')
            : gettext('%(title)s Exam Voucher');
        const voucherTitle = interpolate(titleTemplate, {title: voucherPool.title}, true);
        const voucherItem = buildVoucherShoppingItemFromPool(
            {
                ...voucherPool,
                title: voucherTitle,
                imageUrl: voucherPool.imageUrl ?? '',
                expirationThresholdInMonths: voucherPool.expirationThresholdInMonths ?? 12,
                issuerName: examInTopic.issuerName ?? undefined,
            },
            retakeInsurancePrice ?? selectedPrice ?? undefined,
        );

        if (!voucherItem) return null;

        if (bundle) {
            const other = (bundle as unknown as CertificationBundle).contents.find(
                (c) => c.id !== courseId,
            );
            if (other) {
                return {otherCourseId: other.id, voucherItem};
            }
        }

        return null;
    }, [
        voucherPool,
        bundle,
        courseId,
        selectedPrice,
        retakeInsurancePrice,
        gettext,
        interpolate,
        examInTopic.issuerName,
    ]);

    const suggestedCourseId = resolved?.otherCourseId ?? -1;

    const shouldFetchPrice = suggestedCourseId >= 0 && !cartCourseIds?.has(suggestedCourseId);
    const {price: coursePrice, isLoading: coursePriceLoading} = useCoursePrice(
        shouldFetchPrice ? suggestedCourseId : null,
    );

    const otherCourseItem = useMemo(() => {
        if (!resolved || cartCourseIds?.has(suggestedCourseId) || otherCourseLoading) return null;
        if (otherCourse?.isEnrolled) return null;
        const candidate = otherCourse
            ? buildShoppingItemFromGqlCourse(otherCourse, coursePrice, gettext)
            : null;
        if (candidate && isEffectivelyFree(candidate, coursePriceLoading)) return null;
        return candidate;
    }, [
        resolved,
        cartCourseIds,
        suggestedCourseId,
        otherCourseLoading,
        otherCourse,
        coursePrice,
        coursePriceLoading,
        gettext,
    ]);

    const showSuggestedCourse = !!otherCourseItem;

    const voucherOfferId = (
        resolved?.voucherItem.buyable as CertificationVoucherBuyable | undefined
    )?.offer_id;
    const showVoucher = !!resolved && !cartVoucherOfferIds?.has(voucherOfferId ?? '');

    const isCartCourseFree = item.list_price.amount === 0;
    const isLoading = bundleLoading || examIsLoading || coursePriceLoading || otherCourseLoading;
    const hasContent =
        !isCartCourseFree && !isLoading && !!resolved && (showSuggestedCourse || showVoucher);

    const showConnector =
        showSuggestedCourse &&
        showVoucher &&
        featureVariation === CartUpsellFeatureVariation.Bundled;

    return {
        resolved,
        otherCourseItem,
        showSuggestedCourse,
        showVoucher,
        showConnector,
        hasContent,
        voucherOfferId,
        isLoading,
    };
}
